import { inject, Injectable, DOCUMENT } from "@angular/core";

import { CanonicalUrlService } from "./canonical-url.service";


const scriptId = "app-structured-data";

const publisher = {
  "@type": "GovernmentOrganization",
  name: "Staatskanzlei St.Gallen",
  url: "https://www.sg.ch",
};

export interface BreadcrumbItem {
  name: string;
  path: string;
}

/**
 * Emits a single schema.org `@graph` describing the current page. Navigating clears the previous
 * graph, so a page that publishes nothing is not left advertising its predecessor's data.
 */
@Injectable({ providedIn: "root" })
export class StructuredDataService {
  private readonly document = inject(DOCUMENT);
  private readonly canonicalUrl = inject(CanonicalUrlService);

  clear() {
    this.document.getElementById(scriptId)?.remove();
  }

  setWebSite() {
    const url = this.canonicalUrl.toAbsolute("/");
    this.write([
      {
        "@type": "WebSite",
        "@id": `${url}#website`,
        url,
        name: "Retrodigital",
        alternateName: "Historische Unterlagen St.Gallen",
        inLanguage: "de-CH",
        publisher,
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${url}search?search={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
    ]);
  }

  setPeriodical({ name, description, path, breadcrumbs }: { name: string; description?: string | null; path: string; breadcrumbs: BreadcrumbItem[] }) {
    this.write([
      {
        "@type": "Periodical",
        "@id": `${this.canonicalUrl.toAbsolute(path)}#periodical`,
        name,
        ...(description ? { description } : {}),
        url: this.canonicalUrl.toAbsolute(path),
        inLanguage: "de-CH",
        isAccessibleForFree: true,
        publisher,
      },
      this.breadcrumbList(breadcrumbs),
    ]);
  }

  setBreadcrumbs(breadcrumbs: BreadcrumbItem[]) {
    this.write([this.breadcrumbList(breadcrumbs)]);
  }

  private breadcrumbList(breadcrumbs: BreadcrumbItem[]) {
    return {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: this.canonicalUrl.toAbsolute(crumb.path),
      })),
    };
  }

  private write(graph: object[]) {
    let script = this.document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
  }
}
